import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, X, ArrowUpRight } from "lucide-react";
import { SITE } from "../data/site";
import { PRODUCTS, CATEGORIES } from "../data/products";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/products", label: "Products", mega: true },
  { to: "/why-us", label: "Why Us" },
  { to: "/contact", label: "Contact" },
];

const Logo = () => (
  <Link to="/" data-testid="header-logo" className="flex items-baseline gap-2">
    <span className="font-display text-2xl font-bold tracking-wide text-white">
      TERRA<span className="text-amber-500">BLOOM</span>
    </span>
    <span className="hidden sm:block font-mono text-[10px] tracking-[0.3em] text-slate-500">{SITE.brandSub}</span>
  </Link>
);

export const Header = () => {
  const [megaOpen, setMegaOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setMegaOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-40 border-b border-white/10 bg-[#0A101D]/95 backdrop-blur-xl"
    >
      <div className="mx-auto flex h-18 max-w-7xl items-center justify-between px-5 py-4 lg:px-8">
        <Logo />
        <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
          {NAV.map((item) =>
            item.mega ? (
              <div
                key={item.to}
                className="relative"
                onMouseEnter={() => setMegaOpen(true)}
                onMouseLeave={() => setMegaOpen(false)}
              >
                <NavLink
                  to={item.to}
                  data-testid="nav-products"
                  className={({ isActive }) =>
                    `flex items-center gap-1 text-sm font-medium transition-colors ${
                      isActive ? "text-amber-500" : "text-slate-300 hover:text-white"
                    }`
                  }
                >
                  {item.label}
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform ${megaOpen ? "rotate-180" : ""}`} />
                </NavLink>
                <AnimatePresence>
                  {megaOpen && (
                    <motion.div
                      data-testid="products-mega-menu"
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 12 }}
                      transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                      className="absolute left-1/2 top-full w-[42rem] -translate-x-1/2 pt-4"
                    >
                      <div className="border border-white/10 bg-[#111B2C]/95 backdrop-blur-xl p-6 shadow-[0_24px_60px_rgba(0,0,0,0.5)]">
                        {CATEGORIES.map((cat) => (
                          <div key={cat.id} className="mb-5 last:mb-0">
                            <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500">
                              {cat.label}
                            </p>
                            <div className="grid grid-cols-2 gap-2">
                              {PRODUCTS.filter((p) => p.category === cat.id).map((p) => (
                                <Link
                                  key={p.slug}
                                  to={`/products/${p.slug}`}
                                  data-testid={`mega-menu-${p.slug}`}
                                  className="group flex items-start justify-between gap-3 border border-transparent p-3 transition-colors hover:border-amber-500/40 hover:bg-[#162338]"
                                >
                                  <div>
                                    <p className="text-sm font-semibold text-white">{p.shortName}</p>
                                    <p className="mt-1 text-xs leading-relaxed text-slate-400 line-clamp-2">{p.tagline}</p>
                                  </div>
                                  <ArrowUpRight className="mt-1 h-4 w-4 shrink-0 text-slate-600 transition-colors group-hover:text-amber-500" />
                                </Link>
                              ))}
                            </div>
                          </div>
                        ))}
                        <div>
                          <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500">
                            Steel
                          </p>
                          <Link
                            to="/steel"
                            data-testid="mega-menu-steel"
                            className="group flex items-start justify-between gap-3 border border-transparent p-3 transition-colors hover:border-amber-500/40 hover:bg-[#162338]"
                          >
                            <div>
                              <p className="text-sm font-semibold text-white">Steel Products</p>
                              <p className="mt-1 text-xs leading-relaxed text-slate-400">
                                API 5L line pipe, structural steels, TMT bars and piling pipes.
                              </p>
                            </div>
                            <ArrowUpRight className="mt-1 h-4 w-4 shrink-0 text-slate-600 transition-colors group-hover:text-amber-500" />
                          </Link>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors ${
                    isActive ? "text-amber-500" : "text-slate-300 hover:text-white"
                  }`
                }
              >
                {item.label}
              </NavLink>
            )
          )}
        </nav>
        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="header-quote-button"
            className="hidden sm:inline-flex items-center gap-2 bg-amber-500 px-5 py-2.5 text-sm font-semibold text-[#0A101D] transition-colors hover:bg-amber-400"
          >
            Request a Quote
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
            className="lg:hidden flex h-10 w-10 items-center justify-center border border-white/15 text-white"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {mobileOpen && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden border-t border-white/10 bg-[#0A101D]/95 backdrop-blur-xl"
            aria-label="Mobile"
          >
            <div className="px-5 py-6 space-y-1">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `block py-3 font-display text-2xl font-semibold tracking-wide ${
                      isActive ? "text-amber-500" : "text-white"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <div className="pt-3 grid grid-cols-2 gap-2">
                {PRODUCTS.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/products/${p.slug}`}
                    data-testid={`mobile-nav-${p.slug}`}
                    className="border border-white/10 px-3 py-2 text-xs text-slate-300 hover:border-amber-500/40"
                  >
                    {p.shortName}
                  </Link>
                ))}
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
